import { Component, Inject, OnInit, ViewChild, AfterViewInit, Input, Output, ElementRef, QueryList, ViewChildren } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { SelectCondition } from './model/selectCondition';
import { TableComponent, DataReloadEvent } from '../../table/table.component';
import { TableColumn } from '../../table/table.column';
import { MessageService } from '../../service/message.service';
import { TableElement } from '../../table/table.element';
import { TranslateService } from '@ngx-translate/core';
import { HttpParams } from '@angular/common/http';
import { filter } from 'rxjs/operators';
import { CheckboxComponent } from '../../table/checkbox/checkbox.component';
import { EventEmitter } from '@angular/core';
import { RadioComponent } from '../../table/radio/radio.component';
import { NgForm } from '@angular/forms';

@Component({
	selector: 'app-select-dialog',
	templateUrl: './selectDialog.component.html',
	styleUrls: ['./selectDialog.component.scss']
})
export class SelectDialogComponent implements OnInit, AfterViewInit {

	@Input() part = false;
	@Output() ok = new EventEmitter<any>();
	@Output() close = new EventEmitter<any>();

	title: string;
	query: string;
	method = 'get';
	params: URLSearchParams;
	list: any[];

	isLoading = false;
	search: any[];
	_searchControls: QueryList<ElementRef>;
	@ViewChildren('searchControls', { read: ElementRef }) set searchControls(_searchControls: QueryList<ElementRef>) {
		this._searchControls = _searchControls;

		if (_searchControls) {
			_searchControls.forEach((searchControl: ElementRef, index: number) => {
				searchControl.nativeElement.addEventListener('keypress', (e: KeyboardEvent) => {
					if (e.key === "Enter") {
						this.doSearch();
					}
				 });	
			})
		}
	}
	get searchControls(): QueryList<ElementRef> {
		return this._searchControls;
	}

	filterKey: string;
	excludeFilter: string[];
	includeFilter: string[];

	columns: TableColumn[];
	checkColumn: TableColumn;

	sortBy: string;
	sortDir: string;
	pageNumber: number;
	pageSize = 10;
	pageSizeOptions = [5, 10, 20, 30];

	selectedItems: string[];
	selectedElements: any[];
	selectButtonLabel: string;
	closeButtonLabel: string;

	isSelectionMultiple = false;

	_selectedList: TableComponent;
	@ViewChild('selectList', {static: true}) set selectedList(_selectedList: TableComponent) {
		this._selectedList = _selectedList;
	}
	get selectedList() {
		return this._selectedList;
	}

	@ViewChild('selectForm', {static: true}) private selectForm: NgForm;

	results: any[];
	currentCondition: DataReloadEvent;

	constructor(
		protected messageService: MessageService,
		protected dialogRef: MatDialogRef<SelectDialogComponent>,
		@Inject(MAT_DIALOG_DATA) {
			title,
			query,
			method,
			params,
			search,
			list,
			filterKey,
			excludeFilter,
			includeFilter,
			columns,
			sortBy,
			sortDir,
			pageNumber,
			pageSize,
			pageSizeOptions,
			selectedItems,
			selectButtonLabel,
			closeButtonLabel }: SelectCondition,
		protected translateService: TranslateService
	) {

		this.title = title;
		this.translateService.get(title).subscribe((text: string) => this.title = text);
		this.query = query;
		this.method = method;
		this.search = search;
		this.params = params;
		this.selectedElements = [];

		if (search) {
			this.search = search.map((item: any) => {
				item.value = '';
				return item;
			});
		}

		if (pageSizeOptions) {
			this.pageSizeOptions = pageSizeOptions;
		}
		this.sortDir = (sortDir ? sortDir : 'desc');
		this.sortBy = (sortBy ? sortBy : (columns && columns.length > 0 ? columns[columns.length-1].column : 'createdDate'));
		this.pageNumber = (pageNumber ? pageNumber : 0);
		this.pageSize = (pageSize ? pageSize : 10);

		this.filterKey = filterKey;
		this.excludeFilter = excludeFilter;
		this.includeFilter = includeFilter;
		this.selectButtonLabel = (selectButtonLabel ? selectButtonLabel : 'common.button.select');
		this.closeButtonLabel = (closeButtonLabel ? closeButtonLabel : 'common.button.close');

		this.translateService.get(this.selectButtonLabel).subscribe((text: string) => this.selectButtonLabel = text);
		this.translateService.get(this.closeButtonLabel).subscribe((text: string) => this.closeButtonLabel = text);

		this.list = list;
		this.columns = columns;
		this.selectedItems = selectedItems;

		if (this.columns) {
			this.columns.forEach((column: TableColumn) => {
				column.withTranslate(this.translateService);

				if (column.type === CheckboxComponent) {
					if (column.isIdentifier) {
						column.withHeaderCheck(true);
					}
					this.isSelectionMultiple = true;
					this.checkColumn = column;
				} else if (column.type === RadioComponent) {
					column.withHeaderCheck(false);
					this.isSelectionMultiple = false;
					this.checkColumn = column;
				}
			});
		}


	}

	_filter(selectable: any): boolean {
		let filtered = true;
		if (this.excludeFilter) {
			filtered = !this.excludeFilter.includes(TableColumn._getData(selectable, this.filterKey));
		}
		if (this.includeFilter) {
			filtered = this.includeFilter.includes(TableColumn._getData(selectable, this.filterKey));
		}
		return filtered;
	}

	_initList(condition?: DataReloadEvent) {
		if (this.method === 'get' || this.method === 'query') {
			let params = new HttpParams();

			if (this.params != null) {
				this.params.forEach((value: string, key: string) => {
					params = params.set(key, value);
				});
			}
			if (this.search) {
				this.search.forEach((searchItem: any) => {
					if (searchItem.searchable) {
						const urlParts = this.query.split("&");
						let url = "";

						urlParts.forEach((urlPart: string) => {
							if (!urlPart.startsWith(searchItem.column)) {
								url = url.concat(urlPart);
							}
						});
						this.query = url;
						if (searchItem.alias) {
							params = params.set(searchItem.alias, searchItem.value);
						} else {
							params = params.set(searchItem.column, searchItem.value);
						}
					}
				});
			}
			if (condition) {
				condition.active = (condition.active ? condition.active : this.sortBy);
				condition.sortOrder = (condition.sortOrder ? condition.sortOrder : 'desc');
				condition.pageNumber = (condition.pageNumber ? condition.pageNumber : 0);
				condition.pageSize = (condition.pageSize ? condition.pageSize : 10);
			} else {
				condition = new DataReloadEvent(this.sortBy, 'desc', 0, 10);
			}
			if (!this.results) {
				this.messageService.query(this.query, params).subscribe(result => {
					this._refreshList(result, condition);
				});
			} else {
				this._refreshList(this.results, condition);
			}
		} else if (this.method === 'page') {
			let params = new HttpParams();

			if (this.params != null) {
				this.params.forEach((value: string, key: string) => {
					params = params.set(key, value);
				});
			}
			if (this.search) {
				this.search.forEach((searchItem: any) => {
					if (searchItem.searchable) {
						const urlParts = this.query.split("&");
						let url = "";

						urlParts.forEach((urlPart: string) => {
							if (!urlPart.startsWith(searchItem.column)) {
								url = url.concat(urlPart);
							}
						});
						this.query = url;
						if (searchItem.alias) {
							params = params.set(searchItem.alias, searchItem.value);
						} else {
							params = params.set(searchItem.column, searchItem.value);
						}
					}
				});
			}
			if (condition) {
				params = params.set('sortItem', (condition.active ? condition.active : this.sortBy))
				.set('sortOrder', (condition.sortOrder ? condition.sortOrder : 'desc'))
				.set('pageNumber', (condition.pageNumber ? condition.pageNumber.toString() : '0'))
				.set('pageSize', (condition.pageSize ? condition.pageSize.toString() : '10'));
			} else {
				params = params.set('sortItem', this.sortBy)
				.set('sortOrder', 'desc')
				.set('pageNumber', '0')
				.set('pageSize', '10');
			}
			this.messageService.query(this.query, params).subscribe(result => {
				this._refreshPage(result, condition);
			});
		} else if (this.method === 'post') {
			if (!this.results) {
				this.messageService.post(this.query, null, this.params).subscribe(result => {
					this._refreshList(result, condition);
				});
			} else {
				this._refreshList(this.results, condition);
			}
		} else if (this.method === 'put') {
			if (!this.results) {
				this.messageService.put(this.query, null, this.params).subscribe(result => {
					this._refreshList(result, condition);
				});
			} else {
				this._refreshList(this.results, condition);
			}
		} else if (this.method === 'patch') {
			if (!this.results) {
				this.messageService.patch(this.query, null, this.params).subscribe(result => {
					this._refreshList(result, condition);
				});
			} else {
				this._refreshList(this.results, condition);
			}
		} else if (this.method === 'data') {
			this._refreshList(this.list, condition);
		} else {
			throw new Error('This request has failed due to invalid query method ' + this.method);
		}
	}

	doReload($event: DataReloadEvent) {
		this._initList($event);
	}

	_refreshList(results: any[], condition?: DataReloadEvent) {
		if (results) {
			this.results = results.filter((result : any) => this._filter(result));
			this.currentCondition = condition;
			this._selectedList.refreshData(this.results, this.columns, condition);

			if (this.selectedItems) {
				const selectedItems = this.selectedItems.concat(this.selectedElements.map((selectedItem: any) => selectedItem.id()));
				this._selectedList.setSelectState(selectedItems);
			} else {
				this._selectedList.setSelectState(this.selectedElements.map((selectedItem: any) => selectedItem.id()));
			}
		}
	}

	_refreshPage(result: any, condition?: DataReloadEvent) {
		this._selectedList.refresh(result.totalElements, result.content, this.columns, condition);

		if (this.selectedItems) {
			const selectedItems = this.selectedItems.concat(this.selectedElements.map((selectedItem: any) => selectedItem.id()));
			this._selectedList.setSelectState(selectedItems);
		} else {
			this._selectedList.setSelectState(this.selectedElements.map((selectedItem: any) => selectedItem.id()));
		}
	}

	ngOnInit() {
	}

	onEnterSubmit($event, form: any) {
		if ($event.keyCode === 13) {
			this.doInternalSearch(form);
		}
	}
	
	doSearch() {
		if (this.isLoading == false) {
			if (!this.selectForm.valid) {
				if (this.selectForm.controls) {
					for (const control in this.selectForm.controls) {
						this.selectForm.controls[control].markAsTouched();
					}
				}
				return;
			} else {
				this.results = null;
				this.isLoading = true;
				this._initList();
				this.isLoading = false;
			}	
		}
	}

	doInternalSearch(form: any) {
		this.doSearch();
	}

	ngAfterViewInit() {
		this._initList((this.sortBy ? {
			'active': this.sortBy,
			'sortOrder': this.sortDir,
			'pageNumber': this.pageNumber,
			'pageSize': this.pageSize
		} as DataReloadEvent : null));
	}

	_handleElementSelection(element: any) {
		if (this.isSelectionMultiple) {
			if (element.selected) {
				if (!this.selectedElements.includes(element)) {
					const filtered = this.selectedElements.filter((selectedElement: any) => {
						if (selectedElement.id() && selectedElement.id() === element.id()) {
							return true;
						} else {
							return false;
						}
					});

					if (filtered.length === 0) {
						this.selectedElements.push(element);
					}
				}
			} else {
				for (const [index, selectedElement] of this.selectedElements.entries()) {
					if (selectedElement === element) {
						this.selectedElements.splice(index, 1);
						break;
					} else if (selectedElement.id() && selectedElement.id() === element.id()) {
						this.selectedElements.splice(index, 1);
						break;
					}
				}
			}
		} else {
			this.selectedElements = [];
			this.selectedElements.push(element);
		}
	}

	doHeaderCheckChanged($event: any) {
		this._selectedList.dataSource.getData().forEach((element: any) => {
			this._handleElementSelection(element);
		})
	}

	doButtonClicked($event: any) {
		if ($event.column === this.checkColumn) {
			this._handleElementSelection($event.element);
		}
	}

	doRowClicked($event: any) {
		$event.element.setSelected(!$event.element.selected);

		this._handleElementSelection($event.element);
	}

	protected handleSelect(result: any[]): TableElement[] {
		if (result.length === 0) {
			this.translateService.get('common.dialog.select-dialog.alert.no-item-selected').subscribe((message: string) => {
				alert(message);
			});
			return null;
		} else {
			return result;
		}
	}

	doSelect(selectedElements: any[]) {
		if (this.part) {
			this.ok.emit(this.selectedElements);
		} else {
			const result = this.handleSelect((selectedElements ? selectedElements : this.selectedElements));

			if (result) {
				this.dialogRef.close({
					result: true,
					data: result
				});
			}
		}
	}

	doClose() {
		if (this.part) {
			this.close.emit();
		} else {
			this.dialogRef.close({
				result: false
			});
		}
	}
}
